import React, { Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import apiService from '@/services/api';
import { PostCard } from '@/components/SocialFeed/PostCard';
import { PostFullscreenModal } from '@/components/SocialFeed/PostFullscreenModal';
import { CommentsSection } from '@/components/SocialFeed/CommentsSection';
import styles from './SharedPostView.module.scss';

interface Post {
  id: string;
  title?: string;
  description?: string;
  contentText?: string;
  postType: string;
  attachments?: any[];
  author: any;
  likesCount: number;
  dislikesCount: number;
  commentsCount: number;
  sharesCount: number;
  viewsCount: number;
  isLiked: boolean;
  isDisliked: boolean;
  createdAt: string;
  publishedAt?: string;
  status?: 'draft' | 'published';
  tags?: string[];
}

interface SharedPostViewState {
  post: Post | null;
  loading: boolean;
  error: string | null;
  showFullscreen: boolean;
  showComments: boolean;
}

// Higher-order component to provide navigation and params
function withRouter(WrappedComponent: any) {
  return function WithRouterComponent(props: any) {
    const params = useParams();
    const navigate = useNavigate();
    return <WrappedComponent {...props} params={params} navigate={navigate} />;
  };
}

class SharedPostView extends Component<any, SharedPostViewState> {
  constructor(props: any) {
    super(props);
    this.state = {
      post: null,
      loading: true,
      error: null,
      showFullscreen: false,
      showComments: false,
    };
  }

  componentDidMount() {
    // Store the current URL for redirect after login
    const currentPath = window.location.pathname;
    if (currentPath && currentPath.startsWith('/customer/social/posts/')) {
      sessionStorage.setItem('redirectAfterLogin', currentPath);
      console.log('[SharedPostView] Stored redirect path:', currentPath);
    }
    this.loadPost();
  }

  loadPost = async () => {
    const { postId } = this.props.params;
    
    if (!postId) {
      this.setState({
        error: 'Post ID is missing',
        loading: false,
      });
      return;
    }

    this.setState({ loading: true, error: null });

    try {
      const response = await apiService.getPostById(postId);

      if (response.success && response.data) {
        this.setState({
          post: response.data,
          loading: false,
        });
      } else {
        throw new Error(response.message || 'Failed to load post');
      }
    } catch (error: any) {
      console.error('Failed to load post:', error);
      
      // If it's an authentication error, redirect to login
      // The redirect path is already stored in componentDidMount
      if (error.message?.includes('401') || error.message?.includes('Unauthorized') || error.message?.includes('token')) {
        // Redirect to login - the URL is already stored in sessionStorage
        window.location.href = '/login';
        return;
      }
      
      this.setState({
        error: error.message || 'Post not found or you do not have permission to view it',
        loading: false,
      });
    }
  };

  handlePostUpdate = (postId: string) => {
    // Reload the post to get updated counts
    this.loadPost();
  };

  handlePostClick = (post: Post) => {
    this.setState({ post, showFullscreen: true });
  };

  handleCloseFullscreen = () => {
    this.setState({ showFullscreen: false });
  };

  handleCommentClick = (postId: string) => {
    this.setState({ showComments: true });
  };

  handleCloseComments = () => {
    this.setState({ showComments: false });
  };

  render() {
    const { post, loading, error, showFullscreen, showComments } = this.state;

    return (
      <div className={styles.sharedPostContainer}>
        <div className={styles.header}>
          <button
            className={styles.goBackButton}
            onClick={() => this.props.navigate('/customer/social/feed')}
          >
            <ArrowLeft size={20} />
            <span>Back to Feed</span>
          </button>
          <h1 className={styles.title}>Shared Post</h1>
        </div>

        {loading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>Loading post...</p>
          </div>
        ) : error ? (
          <div className={styles.errorContainer}>
            <p className={styles.errorMessage}>{error}</p>
            <button
              className={styles.retryButton}
              onClick={this.loadPost}
            >
              Retry
            </button>
          </div>
        ) : post ? (
          <div className={styles.postContainer}>
            <PostCard
              post={post}
              onLike={this.handlePostUpdate}
              onDislike={this.handlePostUpdate}
              onComment={this.handleCommentClick}
              onShare={this.handlePostUpdate}
              onFullscreen={this.handlePostClick}
              onView={this.handlePostUpdate}
            />
          </div>
        ) : null}

        {/* Fullscreen Modal */}
        {showFullscreen && post && (
          <PostFullscreenModal
            post={post}
            onClose={this.handleCloseFullscreen}
            onLike={this.handlePostUpdate}
            onDislike={this.handlePostUpdate}
            onComment={this.handleCommentClick}
            onShare={this.handlePostUpdate}
          />
        )}

        {/* Comments Section */}
        {showComments && post && (
          <CommentsSection
            postId={post.id}
            onClose={this.handleCloseComments}
          />
        )}
      </div>
    );
  }
}

export default withRouter(SharedPostView);

